import { useQuery } from '@tanstack/react-query';
import {
  loadViewDataQueryFn,
  type ViewDataParams,
} from 'shared/components/CustomTable/hooks/loadViewData';
import {
  type ApiError,
  addResourceNameToQueryResult,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';

export type GetActionableInvoicesViewParams = ViewDataParams & { view_id: string };

export type ActionableInvoicesView = Awaited<ReturnType<typeof loadViewDataQueryFn>>;

// Per-resource wrapper over the generic views endpoint (loadViewDataQueryFn).
// Replaces the redux-saga `getView` read for the actionable-invoices (to-handle)
// list. Forward-compatible: when the backend exposes a dedicated list endpoint,
// only the queryFn here changes — the consumer keeps the same hook contract.
const emptyActionableInvoices: ActionableInvoicesView = { data: [], metadata: undefined };

export function useGetActionableInvoicesView(params: GetActionableInvoicesViewParams) {
  const axiosInstance = useLegacyAxiosInstance();
  const { view_id, ...filters } = params;

  const queryResult = useQuery<ActionableInvoicesView, ApiError>({
    queryKey: ['actionable-invoices-view', params],
    queryFn: () => loadViewDataQueryFn(axiosInstance, view_id, filters),
    enabled: Boolean(view_id),
    placeholderData: previousData => previousData ?? emptyActionableInvoices,
  });

  return addResourceNameToQueryResult<
    ActionableInvoicesView,
    ApiError,
    'actionableInvoices',
    'PreventUndefined'
  >('actionableInvoices', queryResult, emptyActionableInvoices);
}
